import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import connectApi from 'api/connectApi'
import {
    constants,
    sendMessageToWorker,
    sendMessageToWorkerWithResponse,
    internetConnect,
    paginationHelpers
} from 'helpers'
import {
    hasLocalDataExpired,
    updateLocalDataExpirationTime
} from 'datastores/LocalDataExpiration'
import {
    TransactionsInterface,
    TransactionPayloadType,
    TransactionsResponseType,
    VillageTransactionsStateType,
    TransactionSliceNames
} from 'storeTypes'
import { updateClientsWithTransactions } from 'helpers/updateClientsWithTransactions'
import { refreshVillageClientListAction } from 'features/villageClient/villageClientSlice'

export type FetchTransactionsResult = {
    villageId: string
    transactions: TransactionPayloadType[]
    error?: string
}

export const fetchTransactionsAction = createAsyncThunk<
    FetchTransactionsResult,
    TransactionsInterface,
    { rejectValue: FetchTransactionsResult }
>(
    TransactionSliceNames.transactionFetch,
    async (
        { villageId }: TransactionsInterface,
        { dispatch, rejectWithValue }
    ) => {
        const { TRANSACTIONS } = constants.endpoints
        const { getStoredApiParams, saveCurrentTimestamp, storeApiParams } =
            paginationHelpers('transactions-' + villageId)

        const { transactions: localTransactions } =
            await sendMessageToWorkerWithResponse({
                'get transactions': {
                    villageId
                }
            })

        if (
            !hasLocalDataExpired(
                constants.TRANSACTIONS_EXPIRATION_NAME,
                villageId
            )
        ) {
            await updateClientsWithTransactions(
                villageId,
                localTransactions,
                dispatch,
                undefined,
                refreshVillageClientListAction
            )

            return {
                villageId,
                transactions: localTransactions || []
            }
        }

        if (!internetConnect()) {
            await updateClientsWithTransactions(
                villageId,
                localTransactions,
                dispatch,
                undefined,
                refreshVillageClientListAction
            )

            return rejectWithValue({
                villageId,
                transactions: [],
                error: 'Pas de connexion internet'
            })
        }

        const allRemoteTransactions: TransactionPayloadType[] = []
        let nextKey: string | null | undefined
        let createdSince: string | undefined
        try {
            const { cursor, timestamp } = getStoredApiParams()
            createdSince = timestamp
            nextKey = cursor
            do {
                const {
                    data: {
                        transactions: fetchedTransactions,
                        next_key: nextKeyFromServer
                    }
                }: TransactionsResponseType = await connectApi.get(
                    TRANSACTIONS,
                    {
                        params: {
                            village_ids: villageId,
                            created_since: createdSince,
                            version: 'v2',
                            limit: constants.PAGINATION_BATCH_SIZE,
                            next_key: nextKey
                        }
                    }
                )
                if (fetchedTransactions) {
                    allRemoteTransactions.push(...fetchedTransactions)
                }
                nextKey = nextKeyFromServer
            } while (nextKey != null)
            saveCurrentTimestamp()

            updateLocalDataExpirationTime(
                constants.TRANSACTIONS_EXPIRATION_NAME,
                villageId,
                constants.TRANSACTIONS_EXPIRATION_DURATION
            )

            const combinedTransactions = [
                ...(localTransactions || []),
                ...allRemoteTransactions
            ]

            combinedTransactions.forEach(
                (transaction: TransactionPayloadType) => {
                    // TODO: Remove after we have updated all smss.client references to smss.client_code
                    transaction.client = transaction.client_code
                }
            )

            if (allRemoteTransactions.length > 0) {
                sendMessageToWorker({
                    'store transactions': {
                        transactions: allRemoteTransactions,
                        villageId
                    }
                })
            }

            await updateClientsWithTransactions(
                villageId,
                combinedTransactions,
                dispatch,
                undefined,
                refreshVillageClientListAction
            )

            return {
                villageId,
                transactions: combinedTransactions
            }
        } catch (error: unknown) {
            if (allRemoteTransactions.length > 0) {
                sendMessageToWorker({
                    'store transactions': {
                        transactions: allRemoteTransactions,
                        villageId
                    }
                })
            }

            await updateClientsWithTransactions(
                villageId,
                [...(localTransactions || []), ...allRemoteTransactions],
                dispatch,
                undefined,
                refreshVillageClientListAction
            )
            if (nextKey) {
                storeApiParams(createdSince ?? '', nextKey)
            }

            console.error('Error fetching transactions:', error)

            return rejectWithValue({
                villageId,
                transactions: [],
                error: 'Un problème est survenu'
            })
        }
    }
)

const initialState: VillageTransactionsStateType = {}

const transactionsSlice = createSlice({
    name: TransactionSliceNames.transactions,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchTransactionsAction.pending, (state, action) => {
            const villageId = action.meta.arg?.villageId ?? ''
            state[villageId] = {
                ...(state[villageId] || {}),
                loading: true
            }
        })
        builder.addCase(fetchTransactionsAction.fulfilled, (state, action) => {
            const { villageId, transactions } = action.payload
            state[villageId ?? ''] = {
                ...(state[villageId ?? ''] || {}),
                loading: false,
                transactions: transactions as TransactionPayloadType[]
            }
        })
        builder.addCase(fetchTransactionsAction.rejected, (state, action) => {
            const villageId =
                action.payload?.villageId ?? action.meta.arg?.villageId ?? ''
            const error = action.payload?.error as string
            state[villageId] = {
                ...(state[villageId] || {}),
                loading: false,
                error,
                transactions: []
            }
        })
    }
})

export default transactionsSlice.reducer
